import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  AtSign,
  Copy,
  Check,
  PencilLine,
  Clock,
  ShieldCheck,
  Sparkles,
  Users,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

// Debounce helper — returns a new function that delays execution
const useDebouncedValue = (value, delay = 350) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const formatRemaining = (seconds) => {
  if (!seconds || seconds <= 0) return "available now";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days >= 1) return `${days} day${days === 1 ? "" : "s"} ${hours}h`;
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
};

const AliasRow = ({ label, value, live, notLiveNote, recipients, recipientsLoading }) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — select the text manually");
    }
  };
  const count = recipients?.count;
  const list = recipients?.recipients || [];
  const showRecipients = recipients !== undefined; // admins only
  return (
    <div
      data-testid={`alias-row-${label.toLowerCase()}`}
      className="rounded-lg bg-secondary/40 border border-white/5"
    >
      <div className="flex items-center gap-3 p-3">
        <div className="flex-shrink-0 w-20 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
          {label}
        </div>
        <code className="flex-1 font-mono text-sm break-all text-foreground/90">
          {value}
        </code>
        {live ? (
          <Badge
            variant="secondary"
            className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 text-[10px]"
          >
            Live
          </Badge>
        ) : (
          <Badge
            variant="secondary"
            className="bg-amber-500/10 text-amber-300 border-amber-500/30 text-[10px]"
            title={notLiveNote}
          >
            Pending
          </Badge>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-8"
          data-testid={`copy-alias-${label.toLowerCase()}-btn`}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
      {showRecipients && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center gap-2 px-3 pb-2 pt-0 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid={`alias-recipients-toggle-${label.toLowerCase()}`}
          aria-expanded={expanded}
        >
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
          <Users className="w-3.5 h-3.5" />
          <span>
            {recipientsLoading
              ? "Counting recipients…"
              : count === undefined
                ? "Show recipients"
                : `${count} recipient${count === 1 ? "" : "s"} — ${expanded ? "hide" : "show"} list`}
          </span>
        </button>
      )}
      {showRecipients && expanded && (
        <div
          className="px-3 pb-3 pt-1 border-t border-white/5"
          data-testid={`alias-recipients-list-${label.toLowerCase()}`}
        >
          {recipientsLoading ? (
            <div className="text-xs text-muted-foreground py-2">Loading…</div>
          ) : list.length === 0 ? (
            <div className="text-xs text-muted-foreground py-2 italic">
              No active recipients — nobody will receive mail at this address yet.
            </div>
          ) : (
            <ul className="text-xs space-y-1 max-h-56 overflow-y-auto pr-1">
              {list.map((r) => (
                <li
                  key={r.email}
                  className="flex items-center justify-between gap-2 py-1"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="truncate text-foreground/90">
                      {r.name || "—"}
                    </span>
                    {r.player_type === "info_only" && (
                      <Badge
                        variant="secondary"
                        className="bg-blue-500/10 text-blue-300 border-blue-500/30 text-[9px] py-0 px-1.5 flex-shrink-0"
                      >
                        info-only
                      </Badge>
                    )}
                  </div>
                  <code className="font-mono text-[11px] text-muted-foreground truncate max-w-[55%]">
                    {r.email}
                  </code>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const EmailIdentityCard = ({ isAdmin }) => {
  const [identity, setIdentity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [proposedSlug, setProposedSlug] = useState("");
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recipients, setRecipients] = useState(null);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  const debouncedSlug = useDebouncedValue(proposedSlug, 350);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get("/band/email-identity");
      setIdentity(res.data);
    } catch (e) {
      console.error("email-identity load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecipients = useCallback(async () => {
    if (!isAdmin) return;
    setRecipientsLoading(true);
    try {
      const res = await apiClient.get("/band/alias-recipients");
      setRecipients(res.data);
    } catch (e) {
      console.error("alias-recipients load failed", e);
      setRecipients(null);
    } finally {
      setRecipientsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  // Live availability check whenever the debounced slug changes
  useEffect(() => {
    if (!dialogOpen) return;
    const slug = (debouncedSlug || "").trim();
    if (!slug) {
      setAvailability(null);
      return;
    }
    let cancelled = false;
    setChecking(true);
    apiClient
      .get("/band/slug/check", { params: { slug } })
      .then((r) => {
        if (!cancelled) setAvailability(r.data);
      })
      .catch(() => {
        if (!cancelled)
          setAvailability({
            available: false,
            reason: "Check failed — try again.",
          });
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSlug, dialogOpen]);

  const openDialog = () => {
    setProposedSlug(identity?.slug || "");
    setAvailability(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!availability?.available || availability?.is_current) return;
    setSaving(true);
    try {
      const res = await apiClient.put("/band/slug", {
        slug: availability.slug,
      });
      toast.success("Slug updated");
      setDialogOpen(false);
      setIdentity((prev) => ({
        ...(prev || {}),
        slug: res.data.slug,
        aliases: res.data.aliases,
        slug_changed_at: res.data.next_change_allowed_at
          ? new Date(
              new Date(res.data.next_change_allowed_at).getTime() -
                7 * 86400 * 1000
            ).toISOString()
          : null,
        next_change_allowed_at: res.data.next_change_allowed_at,
        cooldown_remaining_seconds: 7 * 86400,
        previous_slug: res.data.previous_slug,
        previous_slug_expires_at: res.data.previous_slug_expires_at,
      }));
      // Re-fetch for authoritative cooldown_remaining_seconds
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  if (!identity) return null;

  const cooldownActive = (identity.cooldown_remaining_seconds || 0) > 0;

  return (
    <>
      <Card
        className="glass-card border-amber-500/20"
        data-testid="email-identity-card"
      >
        <CardHeader className="border-b border-white/5">
          <CardTitle className="font-heading flex items-center gap-2">
            <AtSign className="w-5 h-5 text-amber-400" />
            Email Identity
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div>
            <p className="text-sm text-muted-foreground">
              Your band's unique email slug controls every inbound address
              we route. Change it anytime — we'll keep your old slug reserved
              for 30 days so people who still have the old address aren't
              routed to a stranger.
            </p>
          </div>

          {/* Current slug display */}
          <div
            className="flex items-center gap-3 p-4 rounded-lg bg-gradient-to-br from-amber-500/5 to-transparent border border-amber-500/20"
            data-testid="current-slug-display"
          >
            <ShieldCheck className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-0.5">
                Current slug
              </div>
              <div className="font-mono text-lg text-foreground break-all">
                {identity.slug || <em className="text-muted-foreground">not set</em>}
              </div>
            </div>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={openDialog}
                disabled={cooldownActive}
                className="border-amber-500/30 hover:bg-amber-500/10"
                data-testid="change-slug-btn"
                title={
                  cooldownActive
                    ? `Cooldown active. Next change in ${formatRemaining(identity.cooldown_remaining_seconds)}.`
                    : "Change slug"
                }
              >
                <PencilLine className="w-4 h-4 mr-1" />
                {cooldownActive ? "Locked" : "Change"}
              </Button>
            )}
          </div>

          {/* Alias preview rows */}
          <div className="space-y-2">
            <AliasRow
              label="Bare"
              value={identity.aliases?.bare || "—"}
              live={!!identity.inbound_live?.bare}
              notLiveNote=""
              recipients={isAdmin ? recipients?.bare : undefined}
              recipientsLoading={recipientsLoading}
            />
            <AliasRow
              label="Committee"
              value={identity.aliases?.committee || "—"}
              live={!!identity.inbound_live?.committee}
              notLiveNote=""
              recipients={isAdmin ? recipients?.committee : undefined}
              recipientsLoading={recipientsLoading}
            />
          </div>

          {/* Cooldown / previous slug banner */}
          {cooldownActive && (
            <div
              className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 text-sm"
              data-testid="cooldown-banner"
            >
              <Clock className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <div className="text-foreground">
                  Next rename available in{" "}
                  <strong>
                    {formatRemaining(identity.cooldown_remaining_seconds)}
                  </strong>
                  .
                </div>
                {identity.previous_slug && (
                  <div className="text-xs text-muted-foreground">
                    Previous slug{" "}
                    <code className="font-mono">{identity.previous_slug}</code>{" "}
                    reserved until{" "}
                    {identity.previous_slug_expires_at
                      ? new Date(
                          identity.previous_slug_expires_at
                        ).toLocaleDateString()
                      : "—"}
                    .
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Info banner */}
          <div
            className="flex items-start gap-3 p-3 rounded-lg bg-secondary/30 border border-white/5 text-xs text-muted-foreground"
            data-testid="phase-banner"
          >
            <Sparkles className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <strong className="text-foreground">Both addresses are live.</strong>{" "}
              The bare address <code className="font-mono">{`{slug}@${identity.domain}`}</code>{" "}
              reaches every active member and info-only player; the committee
              address goes only to members toggled as Committee. Deps and
              inactive players never receive band-wide mail. Replies go directly
              to the original sender.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rename dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-lg"
          data-testid="rename-slug-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <PencilLine className="w-5 h-5 text-amber-400" />
              Change slug
            </DialogTitle>
            <DialogDescription>
              3–30 characters, letters, numbers and single hyphens only.
              Uppercase is allowed for nicer display (e.g. <code className="font-mono">VivoBand</code>).
              After changing, the old slug is reserved for 30 days and you
              can't rename again for 7 days.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="proposed-slug" className="text-xs uppercase tracking-wider text-muted-foreground">
                New slug
              </Label>
              <Input
                id="proposed-slug"
                data-testid="proposed-slug-input"
                autoFocus
                value={proposedSlug}
                onChange={(e) =>
                  setProposedSlug(
                    e.target.value.replace(/[^a-zA-Z0-9-]/g, "")
                  )
                }
                placeholder="VivoBand"
                className="font-mono mt-1.5"
                maxLength={30}
              />
              {/* Live feedback */}
              <div
                className="mt-2 text-sm min-h-[1.5rem]"
                data-testid="slug-availability"
              >
                {checking && (
                  <span className="text-muted-foreground">
                    Checking availability…
                  </span>
                )}
                {!checking && availability && (
                  <>
                    {availability.is_current ? (
                      <span className="text-muted-foreground">
                        This is your current slug.
                      </span>
                    ) : availability.available ? (
                      <span className="text-emerald-400 flex items-center gap-1">
                        <Check className="w-4 h-4" /> {availability.reason}
                      </span>
                    ) : (
                      <span className="text-red-400">
                        {availability.reason}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            {availability?.aliases_preview && !availability.is_current && (
              <div
                className="space-y-2"
                data-testid="proposed-alias-preview"
              >
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  You'll own these addresses
                </div>
                <div className="p-3 rounded-lg bg-secondary/40 border border-white/5 font-mono text-sm space-y-1">
                  <div className="break-all">
                    <span className="text-muted-foreground">Bare:</span>{" "}
                    {availability.aliases_preview.bare}
                  </div>
                  <div className="break-all">
                    <span className="text-muted-foreground">Committee:</span>{" "}
                    {availability.aliases_preview.committee}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              data-testid="cancel-slug-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                saving ||
                checking ||
                !availability?.available ||
                availability?.is_current
              }
              className="bg-amber-500 hover:bg-amber-400 text-black"
              data-testid="save-slug-btn"
            >
              {saving ? "Saving…" : "Save slug"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EmailIdentityCard;
